import { Link } from "react-router-dom";
import { format } from "date-fns";
import { Clock, ArrowUpRight } from "@phosphor-icons/react";

const fmt = (iso) => {
  try {
    return format(new Date(iso), "MMM d, yyyy");
  } catch {
    return "";
  }
};

export function ArticleCardHero({ a }) {
  if (!a) return null;
  return (
    <Link
      to={`/article/${a.slug}`}
      data-testid={`article-card-hero-${a.slug}`}
      className="group block relative overflow-hidden border border-foreground/15 bg-card h-full"
    >
      <div className="aspect-[16/10] md:aspect-[16/11] overflow-hidden bg-muted">
        <img
          src={a.cover_image}
          alt={a.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
        />
      </div>
      <div className="p-6 md:p-8">
        <div className="flex items-center gap-3 mb-3">
          <span className="overline text-primary">{a.category_name}</span>
          <span className="text-foreground/30">·</span>
          <span className="font-mono text-[11px] text-foreground/60">{fmt(a.published_at)}</span>
        </div>
        <h2 className="font-display text-2xl md:text-4xl font-black leading-tight tracking-tight group-hover:text-primary transition-colors duration-200">
          {a.title}
        </h2>
        <p className="mt-3 text-foreground/70 line-clamp-3">{a.subtitle}</p>
        <div className="mt-5 flex items-center gap-4 text-xs font-mono text-foreground/60">
          <span className="inline-flex items-center gap-1"><Clock size={12} />{a.reading_time} min read</span>
          <span>{a.author}</span>
        </div>
      </div>
    </Link>
  );
}

export function ArticleCard({ a }) {
  if (!a) return null;
  return (
    <Link
      to={`/article/${a.slug}`}
      data-testid={`article-card-${a.slug}`}
      className="group block border border-foreground/15 bg-card hover:border-foreground/50 transition-colors duration-200"
    >
      <div className="aspect-[16/10] overflow-hidden bg-muted">
        <img src={a.cover_image} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="overline text-primary">{a.category_name}</span>
          <span className="text-foreground/30">·</span>
          <span className="font-mono text-[10px] text-foreground/60">{fmt(a.published_at)}</span>
        </div>
        <h3 className="font-display text-xl font-bold leading-snug tracking-tight group-hover:text-primary transition-colors duration-200 line-clamp-3">
          {a.title}
        </h3>
        <p className="mt-2 text-sm text-foreground/70 line-clamp-2">{a.excerpt}</p>
        <div className="mt-4 flex items-center justify-between text-xs font-mono text-foreground/50">
          <span className="inline-flex items-center gap-1"><Clock size={11} />{a.reading_time} min</span>
          <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" />
        </div>
      </div>
    </Link>
  );
}

export function ArticleCardList({ a }) {
  if (!a) return null;
  return (
    <Link
      to={`/article/${a.slug}`}
      data-testid={`article-card-list-${a.slug}`}
      className="group flex gap-4 py-4 border-b border-foreground/10 hover:bg-muted/40 transition-colors duration-200"
    >
      <img
        src={a.cover_image}
        alt={a.title}
        loading="lazy"
        className="w-24 h-24 md:w-28 md:h-28 object-cover flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="overline text-primary text-[10px]">{a.category_name}</div>
        <h4 className="font-display font-bold text-lg leading-snug mt-1 group-hover:text-primary transition-colors duration-200 line-clamp-2">
          {a.title}
        </h4>
        <div className="mt-2 text-xs font-mono text-foreground/50 flex gap-3">
          <span>{fmt(a.published_at)}</span>
          <span>·</span>
          <span>{a.reading_time} min read</span>
        </div>
      </div>
    </Link>
  );
}
